import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Map as MapIcon,
  InsertDriveFile,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { collectionFileDisplayUrl } from '../api';
import { labelForLeadSource } from '../constants/leadSources';
import JobEnteredScheduledDates from './JobEnteredScheduledDates';
import { formatJobTime, googleMapsDirectionsUrl } from '../utils/jobSchedule';
import {
  emailMailtoHref,
  formatPaymentMethod,
  jobClientRecord,
  phoneTelHref,
  resolveJobClientName,
  resolveJobEmail,
  resolveJobPhone,
} from '../utils/driverPortalJobContact';
import { formatDriverMoney, parseJobPriceAmount } from '../utils/driverPortalDayTotals';

function DetailRow({ label, children, sx }) {
  if (children == null || children === '' || children === false) return null;
  return (
    <Typography variant="body2" sx={{ mb: 1.25, ...sx }}>
      <Box component="span" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>
        {label}
      </Box>
      <Box component="span" sx={{ color: 'text.primary' }}>
        {children}
      </Box>
    </Typography>
  );
}

function ContactAction({ href, icon, children }) {
  if (!href) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Link
      href={href}
      underline="none"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        py: 1,
        px: 1.5,
        borderRadius: 2,
        fontWeight: 700,
        fontSize: '1rem',
        color: 'primary.main',
        bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(79, 70, 229, 0.15)' : 'rgba(79, 70, 229, 0.08)'),
        border: '1px solid',
        borderColor: 'primary.main',
        minHeight: 48,
        '&:hover': {
          bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(79, 70, 229, 0.25)' : 'rgba(79, 70, 229, 0.12)'),
        },
      }}
    >
      {icon}
      {children}
    </Link>
  );
}

function fileIsImage(file) {
  if (typeof file?.mime_type === 'string' && file.mime_type.startsWith('image/')) {
    return true;
  }
  const name = String(file?.name || file?.path || '').toLowerCase();
  return /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(name);
}

function fileDisplayUrl(file) {
  if (!file) return null;
  if (file?.path) return collectionFileDisplayUrl(file);
  if (file?.url) return file.url;
  return null;
}

export default function DriverPortalJobDetailContent({ job, selectedDay }) {
  if (!job) return null;

  const cr = jobClientRecord(job);
  const phone = resolveJobPhone(job);
  const email = resolveJobEmail(job);
  const telHref = phoneTelHref(phone);
  const mailHref = emailMailtoHref(email);
  const clientName = resolveJobClientName(job);
  const priceN = parseJobPriceAmount(job);
  const priceLabel =
    priceN != null
      ? formatDriverMoney(priceN, job.currency || 'GBP')
      : null;

  const clientAddressParts = [
    cr?.address,
    cr?.city,
    cr?.postal_code,
    cr?.country,
  ].filter(Boolean);
  const clientAddressLine = clientAddressParts.join(', ');

  const files = Array.isArray(job.files) ? job.files : [];
  const imageFiles = files.filter(fileIsImage);
  const otherFiles = files.filter((f) => !fileIsImage(f));

  const status = String(job.status || 'pending').toLowerCase();

  return (
    <Box>
      <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1, color: 'text.secondary' }}>
        Client & contact
      </Typography>
      <Box sx={{ mt: 1, mb: 2.5 }}>
        <DetailRow label="Client">
          {clientName || '—'}
          {cr?.company ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              <BusinessIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
              {cr.company}
            </Typography>
          ) : null}
        </DetailRow>
        {cr?.contact_person ? (
          <DetailRow label="Contact person">
            <PersonIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
            {cr.contact_person}
          </DetailRow>
        ) : null}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
              Phone
            </Typography>
            <ContactAction href={telHref} icon={<PhoneIcon />}>
              {phone || 'No phone'}
            </ContactAction>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.5 }}>
              Email
            </Typography>
            <ContactAction href={mailHref} icon={<EmailIcon />}>
              {email || 'No email'}
            </ContactAction>
          </Box>
        </Box>
        {clientAddressLine ? (
          <DetailRow label="Client address (on file)">{clientAddressLine}</DetailRow>
        ) : null}
        {cr?.notes ? (
          <DetailRow label="Client notes">{cr.notes}</DetailRow>
        ) : null}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1, color: 'text.secondary' }}>
        Job details
      </Typography>
      <Box sx={{ mt: 1, mb: 2 }}>
        <JobEnteredScheduledDates job={job} variant="detail" sx={{ mb: 2 }} />
        <DetailRow label="Scheduled time">
          {formatJobTime(job) || '—'}
          {selectedDay ? ` · ${selectedDay.format('D MMMM YYYY')}` : ''}
        </DetailRow>
        <DetailRow label="Job address">
          {job.address ? (
            <>
              <LocationIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
              {job.address}
            </>
          ) : (
            '—'
          )}
        </DetailRow>
        {job.address && googleMapsDirectionsUrl(job.address) ? (
          <Button
            component="a"
            href={googleMapsDirectionsUrl(job.address)}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlined"
            size="small"
            startIcon={<MapIcon />}
            sx={{ mb: 2, textTransform: 'none' }}
          >
            Open in Google Maps
          </Button>
        ) : null}
        <DetailRow label="Price">{priceLabel || '—'}</DetailRow>
        <DetailRow label="Payment">
          {formatPaymentMethod(job.payment_method) || '—'}
        </DetailRow>
        <DetailRow label="Lead source">
          {labelForLeadSource(job.lead_source) || job.lead_source || '—'}
        </DetailRow>
        <DetailRow label="Waste type">{job.waste_type || '—'}</DetailRow>
        <DetailRow label="Driver assigned">{job.driver || '—'}</DetailRow>
        <DetailRow label="Booked">
          {job.is_booked === true || job.is_booked === 1 || job.is_booked === '1' ? 'Yes' : 'No'}
        </DetailRow>
        <Box sx={{ mb: 1.5 }}>
          <Chip
            label={job.status || 'pending'}
            color={
              status === 'completed'
                ? 'success'
                : status === 'cancelled'
                  ? 'default'
                  : status === 'in_progress'
                    ? 'primary'
                    : 'warning'
            }
            size="small"
          />
          {job.is_problem ? (
            <Chip
              icon={<WarningIcon />}
              label="Problem job"
              color="warning"
              size="small"
              sx={{ ml: 1 }}
            />
          ) : null}
        </Box>
        {job.is_problem && job.problem_reason ? (
          <DetailRow label="Problem reason">{job.problem_reason}</DetailRow>
        ) : null}
        {status === 'cancelled' && job.cancellation_reason ? (
          <DetailRow label="Cancellation reason">{job.cancellation_reason}</DetailRow>
        ) : null}
        <DetailRow label="Office notes">{job.notes || '—'}</DetailRow>
        <DetailRow label="Driver notes">{job.driver_notes || '—'}</DetailRow>
      </Box>

      {files.length > 0 ? (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: 1, color: 'text.secondary' }}>
            Photos & files ({files.length})
          </Typography>
          {imageFiles.length > 0 ? (
            <Box
              sx={{
                mt: 1.5,
                mb: 2,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: 1,
              }}
            >
              {imageFiles.map((file, index) => {
                const url = fileDisplayUrl(file);
                if (!url) return null;
                return (
                  <Box
                    key={`img-${index}`}
                    component="a"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'block',
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box
                      component="img"
                      src={url}
                      alt={file?.name || 'Photo'}
                      sx={{
                        width: '100%',
                        height: 120,
                        objectFit: 'cover',
                        display: 'block',
                      }}
                      loading="lazy"
                    />
                  </Box>
                );
              })}
            </Box>
          ) : null}
          {otherFiles.length > 0 ? (
            <List dense disablePadding>
              {otherFiles.map((file, index) => {
                const url = fileDisplayUrl(file) || '#';
                return (
                  <ListItem
                    key={`file-${index}`}
                    sx={{ py: 0.5, px: 0, alignItems: 'flex-start' }}
                    secondaryAction={
                      url !== '#' ? (
                        <Link href={url} target="_blank" rel="noreferrer" underline="hover" sx={{ fontWeight: 600 }}>
                          Open
                        </Link>
                      ) : null
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <InsertDriveFile fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={file?.name || 'File'} />
                  </ListItem>
                );
              })}
            </List>
          ) : null}
        </>
      ) : null}
    </Box>
  );
}
